import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/userState";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  OutlinedInput,
  Checkbox,
  Fade,
  Zoom
} from "@mui/material";
import { CheckCircle, ShoppingCart, ErrorOutline } from "@mui/icons-material";

const AddProduct = () => {
  const { fetchUserProducts, createSession } = useUser();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No token found");
          return;
        }

        const result = await fetchUserProducts(token);
        if (result.status && result.data) {
          setProducts(result.data);
        } else {
          setError(result.error || "Failed to load products");
        }
      } catch (err) {
        console.error("Error loading products:", err);
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [fetchUserProducts]);

  const handleSubmitOrder = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found");
        return;
      }

      const selectedImageIds = JSON.parse(
        localStorage.getItem("selectedImageId") || "[]"
      );

      const userData = {
        first_name: localStorage.getItem("first_name"),
        last_name: localStorage.getItem("last_name"),
        email: localStorage.getItem("email"),
        mobile: localStorage.getItem("mobile"),
        lash_length_mm: parseFloat(
          localStorage.getItem("lash_length") || "0"
        ),
        curl_type: localStorage.getItem("curl_type"),
        thickness: localStorage.getItem("thickness"),
        inner_length: parseFloat(localStorage.getItem("inner_length") || "0"),
        center_length: parseFloat(localStorage.getItem("center_length") || "0"),
        outer_length: parseFloat(localStorage.getItem("outer_length") || "0"),
        ar_review_template_ids: selectedImageIds,
        additional_products: selectedProducts.map((productId) => ({
          product_id: productId.toString(),
        })),
      };

      const result = await createSession(token, userData);

      if (result.status && result.data) {
        localStorage.setItem(
          "additional_products",
          JSON.stringify(result.data.additional_products || [])
        );
        localStorage.setItem(
          "ar_review_templates",
          JSON.stringify(result.data.ar_review_templates || [])
        );
        localStorage.setItem(
          "orderId",
          result.data.session.id || "ORD" + Date.now()
        );
        navigate("/order-confirmation");
      } else {
        setError(result.error || "Failed to submit order");
      }
    } catch (err) {
      console.error("Error submitting order:", err);
      setError("Failed to submit order");
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedProductNames = () =>
    selectedProducts.map((id) => {
      const product = products.find((p) => p.id === id);
      return product ? product.title : `Product ${id}`;
    });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f9e6ea"
        sx={{ transition: "background-color 0.3s ease" }}
      >
        <Zoom in timeout={600}>
          <CircularProgress
            size={80}
            sx={{
              color: "#6d2e46",
              animation: "spin 1.5s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" }
              }
            }}
          />
        </Zoom>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f9e6ea"
        p={{ xs: 2, sm: 3 }}
      >
        <Fade in timeout={800}>
          <Card
            elevation={12}
            sx={{
              maxWidth: { xs: "100%", sm: 450 },
              width: "100%",
              borderRadius: 4,
              background: "linear-gradient(145deg, #ffffff, #f9e6ea)",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
              border: "2px solid #d32f2f",
              transition: "transform 0.3s ease",
              "&:hover": { transform: "translateY(-4px)" }
            }}
          >
            <CardContent sx={{ textAlign: "center", p: { xs: 3, sm: 4 } }}>
              <Zoom in timeout={1000}>
                <ErrorOutline
                  sx={{
                    fontSize: { xs: 70, sm: 80 },
                    color: "#d32f2f",
                    mb: 2,
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.1)" },
                      "100%": { transform: "scale(1)" }
                    }
                  }}
                />
              </Zoom>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#d32f2f",
                  mb: 1,
                  fontSize: { xs: "1.5rem", sm: "2rem" }
                }}
              >
                Something Went Wrong
              </Typography>
              <Alert
                severity="error"
                sx={{
                  my: 2,
                  borderRadius: 2,
                  bgcolor: "#ffebee",
                  color: "#d32f2f",
                  "& .MuiAlert-icon": { color: "#d32f2f" }
                }}
              >
                {error}
              </Alert>
              <Button
                variant="contained"
                size="large"
                onClick={() => window.location.reload()}
                sx={{
                  mt: 2,
                  bgcolor: "#d32f2f",
                  color: "#fff",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: "#b71c1c",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)"
                  }
                }}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </Fade>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f9e6ea"
      p={{ xs: 2, sm: 3, md: 4 }}
      sx={{ transition: "background-color 0.3s ease" }}
    >
      <Fade in timeout={600}>
        <Card
          elevation={12}
          sx={{
            maxWidth: { xs: "100%", sm: 700, md: 800 },
            width: "100%",
            borderRadius: 4,
            background: "linear-gradient(145deg, #ffffff, #f9e6ea)",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
            border: "2px solid #6d2e46",
            transition: "transform 0.3s ease",
            "&:hover": { transform: "translateY(-4px)" }
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Box textAlign="center" mb={{ xs: 3, sm: 4 }}>
              <Zoom in timeout={800}>
                <ShoppingCart
                  sx={{
                    fontSize: { xs: 50, sm: 70, md: 80 },
                    color: "#6d2e46",
                    mb: 1
                  }}
                />
              </Zoom>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "#6d2e46",
                  mb: 1,
                  fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" }
                }}
              >
                Add Extra Products
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#555",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  maxWidth: 600,
                  mx: "auto"
                }}
              >
                Select any additional items to include with your order.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#6d2e46",
                  mt: 1,
                  fontSize: { xs: "0.8rem", sm: "0.9rem" }
                }}
              >
                This step is optional. You can submit without add-ons or visit settings to add more products.
              </Typography>
            </Box>

            <Divider sx={{ mb: { xs: 3, sm: 4 }, borderColor: "#6d2e46" }} />

            <Box mb={{ xs: 3, sm: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#6d2e46",
                  mb: 2,
                  fontSize: { xs: "1.1rem", sm: "1.25rem" }
                }}
              >
                Additional Items
              </Typography>
              <FormControl fullWidth>
                <InputLabel
                  id="products-select-label"
                  sx={{ color: "#6d2e46", fontWeight: 500 }}
                >
                  Select Products...
                </InputLabel>
                <Select
                  labelId="products-select-label"
                  multiple
                  value={selectedProducts}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedProducts(
                      typeof value === "string"
                        ? value.split(",").map(Number)
                        : value
                    );
                  }}
                  input={
                    <OutlinedInput
                      label="Select Products..."
                      sx={{
                        color: "#6d2e46",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d2e46"
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4a1f31"
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4a1f31"
                        }
                      }}
                    />
                  }
                  sx={{
                    color: "#6d2e46",
                    "& .MuiSvgIcon-root": { color: "#6d2e46" }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const product = products.find((p) => p.id === value);
                        return (
                          <Chip
                            key={value}
                            label={product ? product.title : `Product ${value}`}
                            sx={{
                              color: "#6d2e46",
                              borderColor: "#6d2e46",
                              bgcolor: "#f9e6ea",
                              "&:hover": { bgcolor: "#fce4ec" }
                            }}
                            variant="outlined"
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        color: "#6d2e46",
                        backgroundColor: "#fff"
                      }
                    }
                  }}
                >
                  {products.map((product) => (
                    <MenuItem
                      key={product.id}
                      value={product.id}
                      sx={{ color: "#6d2e46" }}
                    >
                      <Checkbox
                        checked={selectedProducts.indexOf(product.id) > -1}
                        sx={{
                          color: "#6d2e46",
                          "&.Mui-checked": { color: "#6d2e46" }
                        }}
                      />
                      <ListItemText
                        primary={product.title}
                        primaryTypographyProps={{
                          fontSize: { xs: "0.9rem", sm: "1rem" }
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ mb: { xs: 3, sm: 4 }, borderColor: "#6d2e46" }} />

            <Box mb={{ xs: 3, sm: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#6d2e46",
                  mb: 2,
                  fontSize: { xs: "1.1rem", sm: "1.25rem" }
                }}
              >
                Selected Items
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  minHeight: 80,
                  bgcolor: "#fce4ec",
                  borderColor: "#6d2e46",
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)"
                }}
              >
                {selectedProducts.length > 0 ? (
                  <List dense>
                    {getSelectedProductNames().map((name, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle
                            sx={{ color: "#6d2e46" }}
                            fontSize="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={name}
                          primaryTypographyProps={{
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            fontWeight: 500,
                            color: "#6d2e46"
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography
                    sx={{
                      textAlign: "center",
                      py: 2,
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      color: "#6d2e46"
                    }}
                  >
                    No extra products selected.
                  </Typography>
                )}
              </Paper>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: "#ffebee",
                  color: "#d32f2f",
                  "& .MuiAlert-icon": { color: "#d32f2f" }
                }}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmitOrder}
                disabled={submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ShoppingCart />
                  )
                }
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 600,
                  borderRadius: 3,
                  bgcolor: "#6d2e46",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#4a1f31",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)"
                  },
                  "&:disabled": {
                    bgcolor: "#cccccc",
                    color: "#666666"
                  },
                  transition: "all 0.3s ease"
                }}
              >
                {submitting ? "Submitting..." : "Submit Order"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
};

export default AddProduct;